"use client";

import { useEffect, useState } from "react";

type Props = {
	/** Absolute /en URL for this same job, built server-side via `buildJobUrl`. */
	enHref: string;
};

const DISMISS_KEY = "ld_read_en_dismissed";

/**
 * Locale prefixes that mean "this visitor reads Indonesian (or a language
 * close enough that the Indonesian UI isn't a barrier)" — Bahasa Indonesia,
 * Malay, Javanese, Sundanese. Anyone whose `navigator.languages` starts with
 * none of these gets offered the English version of this same job.
 */
const SKIP_PREFIXES = ["id", "ms", "jv", "su"];

const INDONESIAN_TIME_ZONES = new Set([
	"Asia/Jakarta",
	"Asia/Pontianak",
	"Asia/Makassar",
	"Asia/Jayapura",
]);

/**
 * Offered ONLY on `/id/jobs/<slug>` pages (mounted conditionally in
 * page.tsx). PostHog (14d, real humans): non-Indonesian visitors landing on
 * an `/id` job page convert to an apply click at 5.5% vs 36% for Indonesian
 * visitors on the same pages — the Indonesian UI, not the job, is the wall.
 * hreflang can't fix this: it only points at `/en` when a native English
 * summary exists (page.tsx generateMetadata), so foreign searchers still land
 * here. This bar is the client-side fallback for the rest.
 *
 * Renders nothing on the server AND on the first client render (the
 * `mounted` gate) — `navigator.languages` and `localStorage` are
 * client-only, so reading them during the render that has to match SSR
 * output would either crash or hydration-mismatch. It only becomes visible
 * from the effect that follows, exactly like `CookieBanner`'s hydrated gate —
 * an insertion after hydration is not a layout shift.
 */
export function ReadInEnglishBar({ enHref }: Props) {
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		let dismissed = false;
		try {
			dismissed = window.localStorage.getItem(DISMISS_KEY) === "1";
		} catch {
			dismissed = false;
		}
		if (dismissed) return;

		const languages: readonly string[] =
			typeof navigator !== "undefined" &&
			navigator.languages &&
			navigator.languages.length > 0
				? navigator.languages
				: typeof navigator !== "undefined" && navigator.language
					? [navigator.language]
					: [];

		const looksLocal = languages.some((lang) =>
			SKIP_PREFIXES.some((prefix) => lang.toLowerCase().startsWith(prefix)),
		);
		if (looksLocal) return;

		// Many Indonesians run an English-language browser, so the language
		// list alone would nag the core audience. A device clock set to an
		// Indonesian zone means they read the Indonesian UI fine.
		let timeZone = "";
		try {
			timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
		} catch {
			timeZone = "";
		}
		if (INDONESIAN_TIME_ZONES.has(timeZone)) return;

		setVisible(true);
	}, []);

	function handleDismiss() {
		setVisible(false);
		try {
			window.localStorage.setItem(DISMISS_KEY, "1");
		} catch {
			// Storage unavailable (private mode, blocked) — dismissal just won't
			// persist across reloads, not worth failing the click over.
		}
	}

	if (!visible) return null;

	return (
		// translate="no": Google Translate wraps text nodes and calls removeChild
		// on nodes it already moved once React unmounts/remounts them — this bar
		// mounts/unmounts client-side (visible -> false on dismiss), the same
		// removeChild vector documented next to the mobile apply footer in
		// jobs/[slug]/page.tsx.
		<div
			translate="no"
			className="fixed inset-x-0 top-16 z-40 flex justify-center px-4"
		>
			<div className="flex items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-lg">
				<p className="text-sm text-foreground">
					This job page is in Indonesian.{" "}
					<a
						href={enHref}
						data-ph-capture-attribute-surface="read_in_english_bar"
						className="font-semibold text-primary underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						Read it in English →
					</a>
				</p>
				<button
					type="button"
					aria-label="Dismiss"
					onClick={handleDismiss}
					className="shrink-0 rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					×
				</button>
			</div>
		</div>
	);
}
